/**
 * PR / race times are stored as decimal minutes (e.g. 5:24 → 5.4 min).
 * IEEE floats break round-trip: 5.4*60 can be 323.999… → floor → wrong second.
 * We normalize to centisecond precision (stable encode/decode).
 */

const CS_PER_MINUTE = 6000; // 60 sec × 100 centiseconds per minute of duration

/** Stable minutes value for Firestore (centisecond resolution, no drift). */
export function normalizeMinutesForStorage(minutes: number): number {
  return Math.round(Math.max(0, Number(minutes)) * CS_PER_MINUTE) / CS_PER_MINUTE;
}

/** Clock H:M:S (seconds may be decimal) → minutes for storage. */
export function minutesFromUserParts(clockH: number, clockM: number, clockSec: number): number {
  const totalSec100 = Math.round(
    Math.max(
      0,
      (Number(clockH) || 0) * 3600 + (Number(clockM) || 0) * 60 + Number(clockSec)
    ) * 100
  );
  return totalSec100 / CS_PER_MINUTE;
}

function minutesToTotalCentis(minutes: number): number {
  return Math.round(Math.max(0, Number(minutes)) * CS_PER_MINUTE);
}

/**
 * Split stored minutes into clock components (s may be fractional for hundredths).
 */
export function minutesToHMS(minutes: number): { h: number; m: number; s: number } {
  let r = minutesToTotalCentis(minutes);
  const h = Math.floor(r / 360000);
  r %= 360000;
  const m = Math.floor(r / 6000);
  r %= 6000;
  const s = r / 100;
  return { h, m, s };
}

/** Lists / tooltips: "4:47", "4:47.03", "1:06:05" */
export function formatPRTimeDisplay(minutes: number): string {
  return formatMinutesAsRaceTime(minutes);
}

/** Same style as bulk race review: M:SS or M:SS.cc or H:MM:SS… */
export function formatMinutesAsRaceTime(minutes: number): string {
  const cs = minutesToTotalCentis(minutes);
  let r = cs;
  const H = Math.floor(r / 360000);
  r %= 360000;
  const M = Math.floor(r / 6000);
  r %= 6000;
  const sInt = Math.floor(r / 100);
  const cent = r % 100;
  const secStr =
    cent === 0
      ? sInt.toString().padStart(2, '0')
      : `${sInt.toString().padStart(2, '0')}.${cent.toString().padStart(2, '0')}`;
  if (H > 0) {
    return `${H}:${M.toString().padStart(2, '0')}:${secStr}`;
  }
  return `${M}:${secStr}`;
}
